// LocationSelector.tsx
import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { locationData } from '../data/locations';
import { Province, City } from '../types';

interface Props {
    selectedCountry?: string;
    selectedProvince?: string;
    selectedCity?: string;
    onCountryChange: (countryId: string) => void;
    onProvinceChange: (provinceId: string) => void;
    onCityChange: (cityId: string) => void;
    disabled?: boolean;
}

const LocationSelector: React.FC<Props> = ({
                                               selectedCountry,
                                               selectedProvince,
                                               selectedCity,
                                               onCountryChange,
                                               onProvinceChange,
                                               onCityChange,
                                               disabled = false,
                                           }) => {
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [cities, setCities] = useState<City[]>([]);

    // همیشه آرایه‌ها را ایمن بخوان
    const countries = Array.isArray(locationData?.countries) ? locationData.countries : [];

    useEffect(() => {
        if (selectedCountry) {
            const country = countries.find((c) => c.id === selectedCountry);
            setProvinces(country?.provinces || []);
            setCities([]);
        } else {
            setProvinces([]);
            setCities([]);
        }
    }, [selectedCountry, countries]);

    useEffect(() => {
        if (selectedProvince && provinces.length) {
            const province = provinces.find((p) => p.id === selectedProvince);
            setCities(province?.cities || []);
        } else {
            setCities([]);
        }
    }, [selectedProvince, provinces]);

    // نکته مهم: value هرگز undefined نباشد
    const countryValue = selectedCountry ?? '';
    const provinceValue = selectedProvince ?? '';
    const cityValue = selectedCity ?? '';

    const nothingToShow = !countries.length;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Country */}
            <div className="space-y-2">
                <Label htmlFor="country">کشور</Label>
                <Select
                    value={countryValue}
                    onValueChange={onCountryChange}
                    disabled={disabled || nothingToShow}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="کشور انتخاب نشده" />
                    </SelectTrigger>
                    {/* position="popper" + z-index بالا تا پشت چیزی گیر نکند */}
                    <SelectContent position="popper" className="z-[9999]">
                        {countries.map((country) => (
                            <SelectItem key={country.id} value={country.id}>
                                {country.name_native}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Province */}
            <div className="space-y-2">
                <Label htmlFor="province">استان</Label>
                <Select
                    value={provinceValue}
                    onValueChange={(val) => {
                        onProvinceChange(val);
                        // وقتی استان عوض می‌شود، شهر را پاک کن تا ناسازگار نشود
                        if (val !== selectedProvince) onCityChange('');
                    }}
                    disabled={disabled || !countryValue || !provinces.length}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="استان انتخاب نشده" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[9999]">
                        {provinces.map((province) => (
                            <SelectItem key={province.id} value={province.id}>
                                {province.name_native}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* City */}
            <div className="space-y-2">
                <Label htmlFor="city">شهر</Label>
                <Select
                    value={cityValue}
                    onValueChange={onCityChange}
                    disabled={disabled || !provinceValue || !cities.length}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="شهر انتخاب نشده" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[9999]">
                        {cities.map((city) => (
                            <SelectItem key={city.id} value={city.id}>
                                {city.name_native}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};

export default LocationSelector;
